import { defineStore } from 'pinia';
import {
  TitledObject,
  TrucodeReferencesService
} from 'src/services/TrucodeReferencesService';
import { ref } from 'vue';
import { Services } from 'src/services/service-manager';
import {UserSettingsService} from 'src/services/UserSettingsService';

export interface ReferencesHistoryData {
  id: string,
  bookCode?: string,
  bookDescription?: string,
  articleId?: string,
  articleDescription?: string,
  searchTerm?: string,
}

export const useTrucodeReferenceStore = defineStore('trucodereferencestore', () => {

  const currentBook = ref<TitledObject | undefined>(undefined);
  const currentTerm = ref<string | undefined>(undefined);
  function setCurrentBook(book: TitledObject) {
    currentBook.value = book;
    articleSearchResults.value = [];
  }

  const currentArticle = ref<ReferencesHistoryData | undefined>(undefined);
  function setCurrentArticle(article: TitledObject) {
    currentArticle.value = {
      id: article.id,
      bookCode: currentBook?.value?.id,
      bookDescription: currentBook?.value?.title,
      articleId: article.id,
      articleDescription: article.title
    };
  }

  function nextArticle() {
    navigateArticle(true);
  }

  function previousArticle() {
    navigateArticle(false);
  }

  function navigateArticle(isNext: boolean) {
    const maxArticles = articleSearchResults.value?.length??0;
    const currentArticleIndex = articleSearchResults.value?.findIndex(item => item.id === currentArticle.value?.articleId)??0
    const nextArticle = nextArticleIndex(currentArticleIndex, maxArticles, isNext);
    if(articleSearchResults.value) {
      setCurrentArticle(articleSearchResults.value[nextArticle]);
    }
  }

  function nextArticleIndex(currentArticleIndex: number, maxArticles: number, isNext: boolean) {
    if(isNext) {
      return currentArticleIndex >= maxArticles - 1 ? 0 : currentArticleIndex + 1;
    } else {
      return currentArticleIndex <= 0 ? maxArticles - 1 : currentArticleIndex - 1;
    }
  }

  const articleSearchResults = ref<TitledObject[] | undefined>([]);
  const highlightMatchedTerms = ref<boolean>(false);
  const searchBookReferences = ref<TitledObject[] | undefined>(undefined);
  const browseBookReferences = ref<TitledObject[] | undefined>(undefined);
  const bookReferenceYearsMap = ref<Map<string, number[] | undefined> | undefined>(undefined);
  const bookReferenceYearIssueMap = ref<Map<[string, number], TitledObject[] | undefined> | undefined>(undefined);
  const referencesArticlesResponseMap = ref<Map<number, TitledObject[] | undefined> | undefined>(undefined);
  const referenceBookTermSearchMap = ref<Map<[string, string], TitledObject[]> | undefined>(undefined);

  const trucodeReferenceService = Services.service(TrucodeReferencesService);

  async function getSearchBookReference():  Promise<TitledObject[] | undefined> {
    if (!searchBookReferences.value) {
      searchBookReferences.value = await trucodeReferenceService.getBookReference('SEARCH');
    }

    return searchBookReferences.value;
  }

  async function getBrowseBookReference():  Promise<TitledObject[] | undefined> {
    if (!browseBookReferences.value) {
      browseBookReferences.value = await trucodeReferenceService.getBookReference('LIST');
    }

    return browseBookReferences.value;
  }

  async function getBookReferenceYears(bookId: string):  Promise<number[] | undefined> {
    if(!bookReferenceYearsMap.value) {
      bookReferenceYearsMap.value = new Map<string, number[]>();
    }

    let result = bookReferenceYearsMap.value.get(bookId);

    if (!result) {
      result = await trucodeReferenceService.getBookReferenceYears(bookId);
      bookReferenceYearsMap.value.set(bookId, result);
    }

    return result;
  }

  async function getBookReferenceYearIssue(year: number):  Promise<TitledObject[] | undefined> {
    if (!bookReferenceYearIssueMap.value) {
      bookReferenceYearIssueMap.value = new Map<[string, number], TitledObject[] | undefined>();
    }

    let result = bookReferenceYearIssueMap.value.get([<string>currentBook?.value?.id, year]);

    if (!result) {
      result = await trucodeReferenceService.getBookReferenceYearIssue(<string>currentBook?.value?.id, year);
      bookReferenceYearIssueMap.value.set([<string>currentBook?.value?.id, year], result);
    }

    return result;
  }

  async function getReferencesArticle(articleId: string):  Promise<string | undefined> {
    return await trucodeReferenceService.getReferencesArticle(articleId);
  }

  async function getReferencesArticles(issueId: number):  Promise<TitledObject[] | undefined> {
    if(!referencesArticlesResponseMap.value) {
      referencesArticlesResponseMap.value = new Map<number, TitledObject[]>();
    }

    let result = referencesArticlesResponseMap.value.get(issueId);

    if (!result) {
      result = await trucodeReferenceService.getReferencesArticles(issueId);
      referencesArticlesResponseMap.value.set(issueId, result);
    }

    return result;
  }

  async function getReferenceBookTermSearch( term: string): Promise<TitledObject[]> {
    currentTerm.value = term;
    if (!referenceBookTermSearchMap.value) {
      referenceBookTermSearchMap.value = new Map<[string, string], TitledObject[]>();
    }
    const bookId = <string>currentBook?.value?.id;
    let result = referenceBookTermSearchMap.value.get([bookId, term]);

    if (!result) {
      result = await trucodeReferenceService.getReferenceBookTermSearch(bookId, term);
      referenceBookTermSearchMap.value.set([bookId, term], result);
    }

    articleSearchResults.value = result;

    return result;
  }

  function getCurrentTerm() {
    return currentTerm.value;
  }

  async function updateHighlightMatchedTerms(value: boolean) {
    const userSettingsService = Services.service(UserSettingsService);
    await userSettingsService.setUserPreference('highlightMatchedTerms', `${value}`);
    highlightMatchedTerms.value = value;
  }

  async function getHighlightMatchedTermsValueTerms() {
    const userSettingsService = Services.service(UserSettingsService);
    const highlightMatchedTermsValue = await userSettingsService.getUserPreference('highlightMatchedTerms', 'true');
    highlightMatchedTerms.value = !!highlightMatchedTermsValue;
  }

  return {
    highlightMatchedTerms,
    currentBook,
    setCurrentBook,
    currentArticle,
    setCurrentArticle,
    nextArticle,
    previousArticle,
    articleSearchResults,
    updateHighlightMatchedTerms,
    getCurrentTerm,
    getHighlightMatchedTermsValueTerms,
    getSearchBookReference,
    getBrowseBookReference,
    getBookReferenceYears,
    getBookReferenceYearIssue,
    getReferencesArticle,
    getReferencesArticles,
    getReferenceBookTermSearch
  }
})
